import React, { useState } from "react";
import dvdlocator from "../../../apis/dvdlocator";
import { Formik, ErrorMessage } from "formik";
import { Form, Button, Dropdown } from "semantic-ui-react";
import * as Yup from "yup";
import { sanitizeObjectStrings } from "../../../utilities/sanitizeObjectStrings";
import _ from "lodash";
import ProgressBarModal from "../../progress/ProgressBarModal";

const RedErrorText = ({ children }) => (
  <p style={{ color: "red", marginBottom: "3px" }}>{children}</p>
);

const MarkListingInputs = ({ addProduct, addProducts }) => {
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [progressData, setProgressData] = useState({
    total: 1,
    current: 0,
  });

  const dropdownOptions = [
    {
      key: "part_number",
      text: "Part Number",
      value: "part_number",
    },
    {
      key: "an",
      text: "Analyst Notes",
      value: "an",
    },
    {
      key: "amz_queue",
      text: "Amazon Queue",
      value: "amz_queue",
    },

    {
      key: "orders",
      text: "Orders",
      value: "orders",
    },
    {
      key: "listory",
      text: "Listory",
      value: "listory",
    },
    {
      key: "priority",
      text: "Priority",
      value: "priority",
    },
  ];

  const bulkAdd = async (values) => {
    const input = prompt("Input comma/newline separated MSKUs: ");
    if (!input) return;
    const mskus = input
      .replace(/\r?\n/g, ",")
      .replace(/\r/, ",")
      .split(",")
      .map((msku) => msku.trim());
    if (mskus.length < 1) return;

    values = { ...values, MSKU: mskus };
    await handleSubmit(values);
  };

  const validationSchema = Yup.object({
    MSKU: Yup.string().required("Please enter a MSKU."),
    marker: Yup.string().required("Please enter a mark."),
    markingColumn: Yup.string().required("Please enter a column to mark."),
  });

  const handleSubmit = (values, actions) => {
    const isBulkSubmit = Array.isArray(values.MSKU);

    if (isBulkSubmit) return handleBulkSubmit(values, values.MSKU);

    dvdlocator
      .post("/product-titles", { MSKU: values.MSKU })
      .then((res) => {
        const mskuTitle = res.data;

        submitData(
          { ...values, title: mskuTitle[values.MSKU] || "NEEDS TITLE NEW" },
          actions
        );
      })
      .catch((err) => console.log(err));
  };

  const handleBulkSubmit = (values, mskus) => {
    const batchSize = 200;
    const batches = _.chunk(mskus, batchSize);

    setProgressData({ total: mskus.length, current: 0 });
    setShowProgressBar(true);

    // Processing MSKUs in batches
    for (const batch of batches) {
      dvdlocator
        .post("/product-titles", { MSKU: batch })
        .then((res) => {
          const mskuTitles = res.data;

          const products = batch.map((msku) => ({
            ...values,
            MSKU: msku,
            title: mskuTitles[msku] || "NEEDS TITLE NEW",
          }));

          addProducts(products);

          setProgressData((prev) => ({
            ...prev,
            current: prev.current + batch.length,
          }));
        })
        .catch((err) => console.log(err));
    }
  };

  const submitData = (values, actions) => {
    addProduct(values);

    actions.setFieldValue("MSKU", "", false);
    actions.setSubmitting(false);
  };

  return (
    <Formik
      initialValues={{ MSKU: "", marker: "", markingColumn: "" }}
      validationSchema={validationSchema}
      onSubmit={async (values, actions) => {
        values = sanitizeObjectStrings(values);

        await handleSubmit(values, actions);

        actions.setFieldValue("MSKU", "", false);
      }}
    >
      {(formikProps) => (
        <Form widths="equal" onSubmit={formikProps.handleSubmit}>
          <ProgressBarModal
            title="Processing large list.."
            label="MSKUs processed"
            total={progressData.total}
            value={progressData.current}
            progressType="ratio"
            open={showProgressBar}
            setOpen={(val) => setShowProgressBar(val)}
          />
          {/* Inputs */}
          <Form.Group>
            <Form.Field>
              <ErrorMessage name="MSKU" component={RedErrorText} />
              <label className="ui label">MSKU</label>
              <input
                name="MSKU"
                onChange={formikProps.handleChange}
                value={formikProps.values["MSKU"]}
              />
            </Form.Field>
            <Form.Field>
              <ErrorMessage name="markingColumn" component={RedErrorText} />
              <label className="ui label">Enter Marking Column</label>
              <Dropdown
                name="markingColumn"
                placeholder="Column"
                onChange={(e, data) => {
                  formikProps.setFieldTouched("markingColumn", true, false);
                  formikProps.setFieldValue("markingColumn", data.value);
                }}
                value={formikProps.values["markingColumn"]}
                fluid
                selection
                disabled={formikProps.isSubmitting}
                options={dropdownOptions}
              />
            </Form.Field>
            <Form.Field>
              <ErrorMessage name="marker" component={RedErrorText} />
              <label className="ui label">Marker</label>
              <input
                name="marker"
                onChange={formikProps.handleChange}
                value={formikProps.values["marker"]}
              />
            </Form.Field>
          </Form.Group>
          {/* End Inputs */}

          {/* Buttons */}
          <Form.Group inline>
            <Button type="submit" inverted color="blue">
              Push to Queue
            </Button>
            <Button
              type="button"
              onClick={() => {
                formikProps.validateForm().then(async (errors) => {
                  formikProps.setFieldTouched("quantity");
                  formikProps.setFieldTouched("MSKU", false);
                  if (errors["quantity"]) return;
                  const values = sanitizeObjectStrings(formikProps.values);
                  await bulkAdd(values);
                });
              }}
            >
              Bulk Push
            </Button>
          </Form.Group>
          {/* End Buttons */}
        </Form>
      )}
    </Formik>
  );
};

export default MarkListingInputs;
